import React, { CSSProperties, useState } from 'react';
import toast from 'react-hot-toast';
import styled, { ThemeProvider } from 'styled-components';
import API from 'utils/API';
import { PrimaryButton, SecondaryButton } from 'styleguide/Buttons';
import { colors } from 'styleguide/colors';
import { TextArea } from 'styleguide/Inputs';
import { Modal } from 'styleguide/Modal';
import { H3 } from 'styleguide/Texts';
import { observer } from 'mobx-react-lite';
import { themeState } from 'styleguide/theme';
import { stores } from 'state/stores';
import { sendEvent } from 'utils/events';
import { GeneralFeedback } from 'hevy-shared';
import { t } from 'translations/t';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 300px;
  border-radius: 10px;
`;

const UpperContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const LowerContainer = styled.div`
  display: flex;
  margin-top: 20px;
`;

const StyledTextArea = styled(TextArea)`
  width: 100%;
  display: flex;
  flex: 1;
  align-self: stretch;
  padding: 12px 20px;
  border: 1px solid ${colors.greyLight};
  border-radius: 6px;
  align-self: stretch;
`;

interface InputModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackModal = observer(({ isOpen, onClose }: InputModalProps) => {
  const [input, setInput] = useState('');

  return (
    <ThemeProvider theme={themeState.current}>
      <Modal isOpen={isOpen} onClose={onClose}>
        <Container>
          <UpperContainer>
            <H3 style={{ marginBottom: 20 }}>{t('web.feedbackModal.sendUsFeedback')}</H3>
            <div style={{ display: 'flex', width: '100%' }}>
              <StyledTextArea
                placeholder={t('web.feedbackModal.addFeedback')}
                minRows={4}
                value={input}
                onChange={e => {
                  const { value } = e.currentTarget;

                  if (value.length > 800) return;

                  setInput(value);
                }}
              />
            </div>
          </UpperContainer>
          <LowerContainer>
            <SecondaryButton
              style={styles.button}
              title={t('global.cancel')}
              onClick={() => {
                onClose();
                setInput('');
              }}
            />
            <div style={{ width: 20 }} />
            <PrimaryButton
              style={styles.button}
              title={t('global.submit')}
              onClick={async () => {
                onClose();

                const feedback: GeneralFeedback = {
                  platform: 'hevy-web',
                  type: 'general',
                  userId: stores.account.id,
                  message: input.trim(),
                };
                await toast
                  .promise(API.postFeedback(feedback), {
                    loading: t('web.feedbackModal.sendFeedback.loading'),
                    success: t('web.feedbackModal.sendFeedback.success'),
                    error: t('web.feedbackModal.sendFeedback.error'),
                  })
                  .then(() => {
                    sendEvent('feedback', {
                      platform: feedback.platform,
                      type: feedback.type,
                      message: feedback.message,
                    });
                  });

                setInput('');
              }}
              enabled={!!input}
            />
          </LowerContainer>
        </Container>
      </Modal>
    </ThemeProvider>
  );
});

const styles: { [key: string]: CSSProperties } = {
  button: {
    display: 'flex',
    flex: 1,
  },
};
